import type { Gender, WearableConfig, WearableId } from "../types";

/**
 * V1 wearable catalog.
 *
 * NOTE: place the GLB files at the referenced `modelPath`s inside `/public`.
 * Expected paths (relative to `public/`):
 *   - models/male_longsleeve.glb
 *   - models/female_longsleeve.glb
 *
 * The paths below use leading slashes so they resolve against the site root
 * both in `vite dev` and on Vercel production builds.
 */

const DEFAULT_TEXTURE_SIZE = 1024;
const DEFAULT_MATERIAL_NAME = "CustomMat";

/**
 * UV printable regions extracted from the GLB by running
 * `node scripts/extract-uv-layout.mjs`. Regions are in pixel coordinates
 * on a `textureSize` x `textureSize` canvas and describe which sub-rect
 * of the texture is mapped to the shirt's front / back torso panels.
 */

// Long sleeve @ 1024, derived from `scripts/extract-uv-layout.mjs`.
// Both male and female share the same UV layout, so they reuse the
// same printable regions. The box centers are placed on the
// area-weighted UV centroid of each torso panel so an artwork dropped
// with default position appears dead-center on the chest / back.
//
// `right` / `up` are unit vectors in texture-pixel space that point
// along the shirt's horizontal / vertical when viewed from the user's
// side of the mesh. The DCL long-sleeve UV is rotated ~45° in the
// texture, so these basis vectors let the X/Y sliders feel aligned
// with the shirt in the 3D viewer instead of diagonal.
// The ray-cast anchor (the mesh-local point (0, midY, minZ)) resolved
// to texture (739, 720), but that pixel turns out NOT to be the user-
// visible chest centre in the 3D viewer (the DCL long-sleeve UV has
// two overlapping front islands). Empirically, moving the layer by
// (-205, -205) in shirt-space from that anchor lands it dead-centre on
// the chest — which equals a texture offset of (-327.6, -59.2). We
// bake that correction into the box origin so slider (0, 0) spawns a
// new artwork on the real chest.
const LONG_SLEEVE_FRONT_BOX = {
  x: 254,
  y: 503,
  width: 315,
  height: 316,
  right: [0.695, 0.719] as const,
  up: [0.903, -0.43] as const,
};
// Same story as the front: the ray-cast anchor landed on the geometric
// back-centre in 3D but not on the user-visible back-centre in UV space.
// Empirically, the back dead-centre is at shirt-space (123, 455) from
// the old anchor, which equals a texture offset of:
//   dx = 123*(-0.711) + 455*(0.962)  = +350.26
//   dy = 123*(-0.703) + 455*(0.272)  =  +37.29
// Shifting the box origin by that amount moves the box centre from
// (336.5, 326.5) to (686.8, 363.8), so slider (0, 0) now spawns at the
// real back-centre on the 3D model.
const LONG_SLEEVE_BACK_BOX = {
  x: 548,
  y: 224,
  width: 277,
  height: 279,
  right: [-0.711, -0.703] as const,
  up: [0.962, 0.272] as const,
};

export const WEARABLES: WearableConfig[] = [
  {
    id: "longsleeve_male",
    label: "Long Sleeve",
    gender: "male",
    modelPath: "/models/BASE_male_long_sleeve.glb",
    textureSize: DEFAULT_TEXTURE_SIZE,
    materialName: DEFAULT_MATERIAL_NAME,
    frontBox: { ...LONG_SLEEVE_FRONT_BOX },
    backBox: { ...LONG_SLEEVE_BACK_BOX },
  },
  {
    id: "longsleeve_female",
    label: "Long Sleeve",
    gender: "female",
    modelPath: "/models/BASE_female_long_sleeve.glb",
    textureSize: DEFAULT_TEXTURE_SIZE,
    materialName: DEFAULT_MATERIAL_NAME,
    frontBox: { ...LONG_SLEEVE_FRONT_BOX },
    backBox: { ...LONG_SLEEVE_BACK_BOX },
  },
];

export function getWearable(id: WearableId): WearableConfig {
  const w = WEARABLES.find((x) => x.id === id);
  if (!w) {
    throw new Error(`Unknown wearable id: ${id}`);
  }
  return w;
}

export function getWearableFor(gender: Gender): WearableConfig {
  const w = WEARABLES.find((x) => x.gender === gender);
  if (!w) {
    throw new Error(`No wearable configured for gender: ${gender}`);
  }
  return w;
}

export function listWearableLabels(): { id: WearableId; label: string }[] {
  // For V1 there is only "Long Sleeve" but the shape is kept generic.
  const seen = new Set<string>();
  const out: { id: WearableId; label: string }[] = [];
  for (const w of WEARABLES) {
    if (seen.has(w.label)) continue;
    seen.add(w.label);
    out.push({ id: w.id, label: w.label });
  }
  return out;
}
